import { useCallback, useEffect, useRef, useState } from "react";
import { fetchTaskDetail } from "../../models/shared/taskDetailModel";

const isUsableTaskDetail = result => !!result
  && typeof result === "object"
  && !!result.task
  && typeof result.task === "object"
  && (result.task.id != null || result.task.task_id != null || result.task.query_pk != null || result.task.query_id != null);

export function useTaskDetail(queryPk, { userId } = {}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const requestIdRef = useRef(0);
  const activeQueryRef = useRef(null);

  const refetch = useCallback(async () => {
    if (!queryPk) {
      setData(null);
      return null;
    }
    const requestId = ++requestIdRef.current;
    setLoading(true);
    let result = null;
    try {
      result = await fetchTaskDetail(queryPk, userId != null ? { userId } : {});
    } finally {
      if (requestId === requestIdRef.current) setLoading(false);
    }
    // fetchTaskDetail resolves null on any failure (network hiccup, transient
    // backend error) — don't let a failed background refresh wipe out
    // already-displayed, valid data. WorkflowTaskDrawer treats data === null
    // as "no task" and blanks its body, including the nested PriceUpdateDrawer
    // (its card prop derives from task, and PriceUpdateDrawer resets its own
    // parts to [] the moment card?.id goes away) — with no way to recover
    // short of closing and reopening. Keeping the stale-but-valid data instead
    // means a flaky refetch just silently retries next time instead of
    // blanking the UI.
    // Empty/malformed successful responses are just as unsafe as a rejected
    // refresh: accepting one removes data.task and blanks both the task drawer
    // and its nested Price Update drawer. Only the newest usable response may
    // replace the currently rendered canonical detail.
    if (requestId === requestIdRef.current && isUsableTaskDetail(result)) setData(result);
    return result;
  }, [queryPk, userId]);

  useEffect(() => {
    const normalizedQueryPk = queryPk == null ? null : String(queryPk);
    const queryChanged = activeQueryRef.current !== normalizedQueryPk;
    activeQueryRef.current = normalizedQueryPk;
    if (queryChanged) {
      requestIdRef.current += 1;
      setData(null);
    }
    if (!queryPk) {
      setLoading(false);
      return;
    }
    refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryPk, userId]);

  return { data, loading, refetch };
}
